<!--
 * @Description: 弹窗内的工具选择器组件
 * @Date: 2026-01-12
-->
<template>
    <div class="mobile-tool-selector-modal" v-click-outside="closeDropdown">
        <!-- 触发器 -->
        <div class="selector-trigger" @click="toggleDropdown">
            <!-- 根据当前工具类型显示分类图标 -->
            <svg v-if="currentToolCategory === 'image'" class="trigger-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 21q-.825 0-1.413-.587T3 19V5q0-.825.588-1.413T5 3h14q.825 0 1.413.588T21 5v14q0 .825-.587 1.413T19 21H5Zm0-2h14V5H5v14Zm1-2h12l-3.75-5-3 4L9 13l-3 4Zm2-7q.625 0 1.063-.437T9.5 8.5q0-.625-.437-1.063T8 7q-.625 0-1.063.438T6.5 8.5q0 .625.438 1.063T8 10Z"/>
            </svg>
            <svg v-else-if="currentToolCategory === 'video'" class="trigger-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 20q-.825 0-1.413-.587T2 18V6q0-.825.588-1.413T4 4h12q.825 0 1.413.588T18 6v4.5l4-4v11l-4-4V18q0 .825-.587 1.413T16 20H4Zm0-2h12V6H4v12Zm0 0V6v12Z"/>
            </svg>
            <svg v-else class="trigger-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6Zm-2 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z"/>
            </svg>
            <span class="trigger-text">{{ currentToolName }}</span>
            <span class="trigger-arrow" :class="{ 'is-open': isDropdownOpen }">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </span>
        </div>

        <!-- 下拉列表 -->
        <Transition name="dropdown">
            <div v-if="isDropdownOpen" class="selector-dropdown">
                <!-- AI 图像分类 -->
                <div class="dropdown-group">
                    <div class="group-header">
                        <svg class="group-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M5 21q-.825 0-1.413-.587T3 19V5q0-.825.588-1.413T5 3h14q.825 0 1.413.588T21 5v14q0 .825-.587 1.413T19 21H5Zm0-2h14V5H5v14Zm1-2h12l-3.75-5-3 4L9 13l-3 4Zm2-7q.625 0 1.063-.437T9.5 8.5q0-.625-.437-1.063T8 7q-.625 0-1.063.438T6.5 8.5q0 .625.438 1.063T8 10Z"/>
                        </svg>
                        <span class="group-title">{{ t('menus.AIImages') }}</span>
                    </div>
                    <div class="group-items">
                        <div
                            v-for="item in imageTools"
                            :key="item.type"
                            class="dropdown-item"
                            :class="{ 'is-selected': currentTool === item.type }"
                            @click="handleSelect(item.type)"
                        >
                            <span class="item-text">{{ item.name }}</span>
                            <svg
                                v-if="currentTool === item.type"
                                class="item-check-svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M5 12l5 5L20 7" stroke="#99f768" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- AI 视频分类 -->
                <div class="dropdown-group">
                    <div class="group-header">
                        <svg class="group-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 20q-.825 0-1.413-.587T2 18V6q0-.825.588-1.413T4 4h12q.825 0 1.413.588T18 6v4.5l4-4v11l-4-4V18q0 .825-.587 1.413T16 20H4Zm0-2h12V6H4v12Zm0 0V6v12Z"/>
                        </svg>
                        <span class="group-title">{{ t('menus.AIVideo') }}</span>
                    </div>
                    <div class="group-items">
                        <div
                            v-for="item in videoTools"
                            :key="item.type"
                            class="dropdown-item"
                            :class="{ 'is-selected': currentTool === item.type }"
                            @click="handleSelect(item.type)"
                        >
                            <span class="item-text">{{ item.name }}</span>
                            <svg
                                v-if="currentTool === item.type"
                                class="item-check-svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M5 12l5 5L20 7" stroke="#99f768" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- 音乐分类 -->
                <div class="dropdown-group">
                    <div class="group-header">
                        <svg class="group-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6Zm-2 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z"/>
                        </svg>
                        <span class="group-title">{{ t('menus.music') }}</span>
                    </div>
                    <div class="group-items">
                        <div
                            v-for="item in musicTools"
                            :key="item.type"
                            class="dropdown-item"
                            :class="{ 'is-selected': currentTool === item.type }"
                            @click="handleSelect(item.type)"
                        >
                            <span class="item-text">{{ item.name }}</span>
                            <svg
                                v-if="currentTool === item.type"
                                class="item-check-svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M5 12l5 5L20 7" stroke="#99f768" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from '~/composables/useI18n'
import type { ToolType } from '~/stores/creationModal'

const props = defineProps<{
    currentTool: ToolType
}>()

const emit = defineEmits<{
    (e: 'change', tool: ToolType): void
    (e: 'navigate', path: string): void
}>()

// 需要跳转到独立页面的工具（这些工具没有可复用的 Setting 组件）
const navigateTools: Record<string, string> = {
    'video-to-video': '/video-to-video',
    'video-effects': '/video-effects',
    'photo-effects': '/photo-effects',
    'ai-tools': '/ai-tools',
    'character-image': '/character-to-image',
    'character-video': '/character-to-video',
}

const { t } = useI18n()

// 下拉框状态
const isDropdownOpen = ref(false)

// 图片工具列表
const imageTools = computed(() => [
    {
        type: 'text-to-image' as ToolType,
        name: t('menus.textToImage'),
    },
    {
        type: 'image-to-image' as ToolType,
        name: t('menus.imageToImage'),
    },
    {
        type: 'character-image' as ToolType,
        name: t('characterToImg.characterToImage'),
    },
    {
        type: 'ai-tools' as ToolType,
        name: t('menus.AITools'),
    },
    {
        type: 'photo-effects' as ToolType,
        name: t('menus.photoEffects'),
    },
])

// 视频工具列表
const videoTools = computed(() => [
    {
        type: 'text-to-video' as ToolType,
        name: t('menus.textToVideo'),
    },
    {
        type: 'image-to-video' as ToolType,
        name: t('menus.imageToVideo'),
    },
    {
        type: 'character-video' as ToolType,
        name: t('characterToImg.characterToVideo'),
    },
    {
        type: 'video-to-video' as ToolType,
        name: t('menus.videoToVideo'),
    },
    {
        type: 'ai-tools' as ToolType,
        name: t('menus.AITools'),
    },
    {
        type: 'video-effects' as ToolType,
        name: t('menus.videoEffects'),
    },
])

// 音乐工具列表
const musicTools = computed(() => [
    {
        type: 'text-to-music' as ToolType,
        name: t('menus.generateMusic'),
    },
])

// 所有工具列表（用于查找当前工具）
const allTools = computed(() => [
    ...imageTools.value,
    ...videoTools.value,
    ...musicTools.value,
])

// 当前工具所属分类
const currentToolCategory = computed(() => {
    const isImageTool = imageTools.value.some(tool => tool.type === props.currentTool)
    const isVideoTool = videoTools.value.some(tool => tool.type === props.currentTool)
    if (isImageTool) return 'image'
    if (isVideoTool) return 'video'
    return 'music'
})

// 当前工具名称
const currentToolName = computed(() => {
    const tool = allTools.value.find(t => t.type === props.currentTool)
    return tool?.name || t('menus.textToImage')
})

// 切换下拉框
const toggleDropdown = () => {
    isDropdownOpen.value = !isDropdownOpen.value
}

// 关闭下拉框
const closeDropdown = () => {
    isDropdownOpen.value = false
}

// 选择工具
const handleSelect = (tool: ToolType) => {
    closeDropdown()
    
    // 检查是否需要跳转到独立页面
    const navigatePath = navigateTools[tool]
    if (navigatePath) {
        emit('navigate', navigatePath)
        return
    }
    
    emit('change', tool)
}

// 点击外部关闭指令
interface ClickOutsideElement extends HTMLElement {
    clickOutsideEvent?: (event: Event) => void
}

const vClickOutside = {
    mounted(el: ClickOutsideElement, binding: any) {
        el.clickOutsideEvent = (event: Event) => {
            if (!(el === event.target || el.contains(event.target as Node))) {
                binding.value()
            }
        }
        document.addEventListener('click', el.clickOutsideEvent)
    },
    unmounted(el: ClickOutsideElement) {
        if (el.clickOutsideEvent) {
            document.removeEventListener('click', el.clickOutsideEvent)
        }
    },
}
</script>

<style scoped lang="less">
.mobile-tool-selector-modal {
    position: relative;
    display: inline-flex;
}

.selector-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: rgba(38, 38, 42, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;

    &:active {
        opacity: 0.8;
        transform: scale(0.98);
    }

    .trigger-icon-svg {
        width: 16px;
        height: 16px;
        color: #99f768;
        flex-shrink: 0;
    }

    .trigger-text {
        color: #ffffff;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
    }

    .trigger-arrow {
        color: rgba(255, 255, 255, 0.6);
        display: flex;
        align-items: center;
        transition: transform 0.3s ease;

        &.is-open {
            transform: rotate(180deg);
        }
    }
}

.selector-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    min-width: 200px;
    max-width: calc(100vw - 32px);
    max-height: 60vh;
    overflow-y: auto;
    background: #1a1a1e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 8px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    z-index: 1000;

    // 隐藏滚动条但保持滚动功能
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.dropdown-group {
    &:not(:last-child) {
        margin-bottom: 8px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }
}

.group-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;

    .group-icon {
        width: 16px;
        height: 16px;
        color: #99f768;
    }

    .group-title {
        color: rgba(255, 255, 255, 0.5);
        font-size: 12px;
        font-weight: 500;
    }
}

.group-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;

    .item-text {
        flex: 1;
        color: #ffffff;
        font-size: 14px;
        font-weight: 400;
    }

    .item-check-svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }

    &.is-selected {
        background: rgba(153, 247, 104, 0.1);

        .item-text {
            color: #99f768;
            font-weight: 500;
        }
    }
}

// 下拉动画
.dropdown-enter-active {
    animation: dropdown-in 0.25s ease-out;
}

.dropdown-leave-active {
    animation: dropdown-out 0.2s ease-in;
}

@keyframes dropdown-in {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

@keyframes dropdown-out {
    from {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
    to {
        opacity: 0;
        transform: translateX(-50%) translateY(-8px);
    }
}
</style>
